import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Hash } from "lucide-react";

import { mock } from "@/mock";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const formatDate = (iso) => {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const Illustration = ({ seed }) => {
  // High-fidelity “vector-ish” placeholder: no stock photos.
  const map = {
    a: "bg-[radial-gradient(circle_at_25%_20%,rgba(16,185,129,0.22),transparent_45%),radial-gradient(circle_at_70%_70%,rgba(139,92,246,0.18),transparent_50%)]",
    b: "bg-[radial-gradient(circle_at_30%_25%,rgba(245,158,11,0.22),transparent_50%),radial-gradient(circle_at_70%_75%,rgba(20,184,166,0.16),transparent_55%)]",
  };
  return (
    <div className="relative h-48 overflow-hidden rounded-2xl border border-white/10 bg-black/30">
      <div
        aria-hidden="true"
        className={
          "absolute inset-0 " + (map[seed] || map.a)
        }
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(0deg,rgba(255,255,255,0.06)_1px,transparent_1px)] bg-[size:18px_18px]"
      />
      <div className="relative flex h-full items-center justify-center">
        <div className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-[12px] text-white/55">
          Vector illustration placeholder
        </div>
      </div>
    </div>
  );
};

export default function Blog() {
  const posts = useMemo(() => mock.blog, []);
  const [active, setActive] = useState(posts[0]?.id || null);

  const selected = posts.find((p) => p.id === active) || posts[0];

  return (
    <div className="pb-20">
      <section className="mx-auto max-w-6xl px-6 pt-10 sm:px-10 sm:pt-16">
        <Badge className="w-fit border-white/10 bg-white/5 text-white/75">
          Blog
        </Badge>
        <h1 className="mt-4 text-balance text-4xl font-semibold tracking-[-0.03em] text-white sm:text-6xl">
          The Synthetic — The future in a wall of words
        </h1>
        <p className="mt-4 max-w-2xl text-[15px] leading-7 text-white/60">
          We’ll share updates from the Smaughk team, lessons from building
          Synthetic Environments, and the things we find genuinely cool in the
          infra + developer tooling space.
        </p>

        <div className="mt-10 grid gap-4 lg:grid-cols-[0.95fr_1.05fr]">
          {/* List */}
          <div className="space-y-4">
            {posts.map((p, idx) => {
              const isActive = p.id === selected?.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setActive(p.id)}
                  className={
                    "w-full text-left transition-colors " +
                    (isActive ? "" : "")
                  }
                  style={{ transition: "transform 180ms ease, opacity 180ms ease" }}
                >
                  <Card
                    className={
                      "overflow-hidden rounded-2xl border p-5 backdrop-blur-xl " +
                      (isActive
                        ? "border-emerald-400/25 bg-emerald-400/10"
                        : "border-white/10 bg-white/[0.03] hover:bg-white/[0.05]")
                    }
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-[14px] font-medium text-white">
                        {p.title}
                      </div>
                      <div className="text-[12px] text-white/50">
                        {p.readingTime}
                      </div>
                    </div>

                    <div className="mt-2 text-[13px] leading-6 text-white/60">
                      {p.excerpt}
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[11px] text-white/55">
                        <Calendar className="h-3.5 w-3.5" /> {formatDate(p.date)}
                      </span>
                      {p.tags.map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[11px] text-white/55"
                        >
                          <Hash className="h-3.5 w-3.5" /> {t}
                        </span>
                      ))}
                    </div>
                  </Card>
                </button>
              );
            })}
          </div>

          {/* Detail */}
          <div>
            {selected && (
              <Card className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-0 backdrop-blur-xl">
                <div className="border-b border-white/10 bg-white/[0.02] px-5 py-4">
                  <div className="text-[15px] font-medium text-white">
                    {selected.title}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-white/55">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-4 w-4" /> {formatDate(selected.date)}
                    </span>
                    <span>•</span>
                    <span>{selected.readingTime}</span>
                  </div>
                </div>

                <div className="px-5 py-5">
                  <Illustration seed={selected.id === posts[0]?.id ? "a" : "b"} />

                  <div className="mt-5 text-[14px] leading-7 text-white/65">
                    {selected.body}
                  </div>

                  <div className="mt-6 rounded-2xl border border-white/10 bg-black/20 p-4">
                    <div className="text-[13px] font-medium text-white/80">
                      more soon
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
